import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import config from "../../../../config.json";
import { getFranchiseId } from "../../../utils/franchiseSession";
import { getAuthToken } from "../../../utils/authToken";
import "../../../assets/css/franchise-portal.css";

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatMoney(value) {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function FranchiseRestaurants() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOnRequestId, setActingOnRequestId] = useState(null);

  const franchiseId = getFranchiseId();

  const loadData = useCallback(async () => {
    if (!franchiseId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [response, joinResponse] = await Promise.all([
        axios.get(`${config.dev}/franchise/${franchiseId}/statistics`, {
          headers: authHeaders(),
        }),
        axios.get(`${config.dev}/franchise/${franchiseId}/join-requests`, {
          headers: authHeaders(),
        }),
      ]);
      setStats(response.data?.data || null);
      setJoinRequests(Array.isArray(joinResponse.data?.data) ? joinResponse.data.data : []);
    } catch (error) {
      console.error("Failed to load franchise statistics:", error);
      swal("Error", t("franchiseLoadError"), "error");
      setStats(null);
      setJoinRequests([]);
    } finally {
      setLoading(false);
    }
  }, [franchiseId, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const summary = stats?.summary;
  const restaurants = stats?.restaurants || [];
  const franchise = stats?.franchise;

  const limitLabel = useMemo(() => {
    if (!franchise?.nbrF) return null;
    return `${franchise.nbrR || 0} / ${franchise.nbrF}`;
  }, [franchise]);

  const buildRestaurantAdminUrl = (accessToken, targetRestaurantId) =>
    `${window.location.origin}/dashboard?token=${encodeURIComponent(
      accessToken
    )}&restaurantId=${encodeURIComponent(targetRestaurantId)}&_imp=${Date.now()}`;

  const handleOpenRestaurantAdmin = async (restaurantId) => {
    const tabName = `franchise-restaurant-${restaurantId}-${Date.now()}`;
    const newTab = window.open("about:blank", tabName);

    if (newTab) {
      try {
        newTab.document.title = t("franchiseOpeningAdmin");
        newTab.document.body.innerHTML = `<p style="font-family:sans-serif;padding:24px;">${t(
          "franchiseOpeningAdmin"
        )}</p>`;
      } catch {
        // Cross-origin restrictions are not expected for about:blank
      }
    }

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${config.dev}/user/v2/franchise/access/${restaurantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data?.token) {
        newTab?.close();
        swal("Error", t("franchiseOpenError"), "error");
        return;
      }

      const adminUrl = buildRestaurantAdminUrl(
        response.data.token,
        response.data.restaurantId || restaurantId
      );

      if (newTab) {
        newTab.location.replace(adminUrl);
        return;
      }

      const openHere = await swal({
        title: t("franchisePopupBlocked"),
        text: t("franchiseOpenSameTabConfirm"),
        icon: "warning",
        buttons: [t("franchiseJoinOpenLater"), t("franchiseOpenAdmin")],
      });

      if (openHere) {
        window.location.href = adminUrl;
      }
    } catch (error) {
      newTab?.close();
      swal(
        "Error",
        error.response?.data?.message || t("franchiseOpenError"),
        "error"
      );
    }
  };

  const handleJoinDecision = async (requestId, action) => {
    setActingOnRequestId(requestId);
    try {
      if (action === "accept") {
        const response = await axios.post(
          `${config.dev}/franchise/join-request/accept`,
          { requestId },
          { headers: authHeaders() }
        );
        swal("Success", t("franchiseJoinAccepted"), "success");
        await loadData();
        const acceptedRestaurantId =
          response.data?.data?.restaurantId?._id ||
          joinRequests.find((item) => item._id === requestId)?.restaurantId?._id;
        if (acceptedRestaurantId) {
          const openNow = await swal({
            title: t("franchiseJoinOpenNowTitle"),
            text: t("franchiseJoinOpenNowText"),
            icon: "success",
            buttons: [t("franchiseJoinOpenLater"), t("franchiseOpenAdmin")],
          });
          if (openNow) {
            void handleOpenRestaurantAdmin(acceptedRestaurantId);
          }
        }
      } else {
        await axios.post(
          `${config.dev}/franchise/join-request/${requestId}/reject`,
          {},
          { headers: authHeaders() }
        );
        swal("Success", t("franchiseJoinRejected"), "success");
      }
      void loadData();
    } catch (error) {
      swal(
        "Error",
        error.response?.data?.error || t("franchiseJoinActionFailed"),
        "error"
      );
    } finally {
      setActingOnRequestId(null);
    }
  };

  return (
    <div className="container-fluid py-4 franchise-portal">
      <div className="franchise-portal__hero">
        <div>
          <h4>{franchise?.nameFr || t("franchisePortalTitle")}</h4>
          <p>{t("franchisePortalSubtitle")}</p>
          {limitLabel ? (
            <small className="text-muted">
              {t("franchiseRestaurantLimit", { count: limitLabel })}
            </small>
          ) : null}
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => void loadData()}>
          {t("franchiseRefresh")}
        </button>
      </div>

      {loading ? (
        <p className="text-muted">{t("franchiseLoading")}</p>
      ) : (
        <>
          <div className="franchise-portal__stats">
            <div className="franchise-portal__stat-card">
              <span>{t("franchiseStatRestaurants")}</span>
              <strong>{summary?.restaurantCount || 0}</strong>
              <small>{t("franchiseStatActive", { count: summary?.activeRestaurants || 0 })}</small>
            </div>
            <div className="franchise-portal__stat-card">
              <span>{t("franchiseStatOrdersToday")}</span>
              <strong>{summary?.ordersToday || 0}</strong>
            </div>
            <div className="franchise-portal__stat-card">
              <span>{t("franchiseStatOrdersWeek")}</span>
              <strong>{summary?.ordersThisWeek || 0}</strong>
            </div>
            <div className="franchise-portal__stat-card">
              <span>{t("franchiseStatPending")}</span>
              <strong>{summary?.pendingOrders || 0}</strong>
            </div>
            <div className="franchise-portal__stat-card">
              <span>{t("franchiseStatRevenueWeek")}</span>
              <strong>{formatMoney(summary?.revenueThisWeek || 0)}</strong>
            </div>
          </div>

          {joinRequests.length > 0 ? (
            <div className="franchise-portal__table-card mb-4">
              <div className="franchise-portal__table-head">
                <h5>{t("franchiseJoinRequestsTitle")}</h5>
                <p>{t("franchiseJoinRequestsDesc")}</p>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>{t("franchiseColRestaurant")}</th>
                        <th>{t("franchiseColCity")}</th>
                        <th>{t("franchiseJoinCodeLabel")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {joinRequests.map((request) => (
                        <tr key={request._id}>
                          <td className="fw-semibold">
                            {request.restaurantId?.nameRes || "-"}
                          </td>
                          <td>{request.restaurantId?.city || "-"}</td>
                          <td>
                            <code>{request.codeFr}</code>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm me-2"
                              disabled={actingOnRequestId === request._id}
                              onClick={() => handleJoinDecision(request._id, "accept")}
                            >
                              {t("franchiseJoinAccept")}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              disabled={actingOnRequestId === request._id}
                              onClick={() => handleJoinDecision(request._id, "reject")}
                            >
                              {t("franchiseJoinReject")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}

          <div className="franchise-portal__table-card">
            <div className="franchise-portal__table-head">
              <h5>{t("franchiseRestaurantsTitle")}</h5>
              <p>{t("franchiseRestaurantsDesc")}</p>
            </div>
            <div className="card-body pt-0">
              {restaurants.length === 0 ? (
                <p className="text-muted mb-0">{t("franchiseNoRestaurants")}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>{t("franchiseColRestaurant")}</th>
                        <th>{t("franchiseColCity")}</th>
                        <th>{t("franchiseColOrdersWeek")}</th>
                        <th>{t("franchiseColRevenueWeek")}</th>
                        <th>{t("franchiseColPending")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {restaurants.map((restaurant) => (
                        <tr key={restaurant._id}>
                          <td className="fw-semibold">
                            {restaurant.nameRes}
                            {restaurant.archive ? (
                              <span className="badge bg-light text-muted border ms-2">
                                {t("franchiseArchived")}
                              </span>
                            ) : null}
                          </td>
                          <td>{restaurant.city || "-"}</td>
                          <td>{restaurant.ordersThisWeek || 0}</td>
                          <td>{formatMoney(restaurant.revenueThisWeek || 0)}</td>
                          <td>{restaurant.pendingOrders || 0}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm franchise-portal__open-btn"
                              onClick={() =>
                                handleOpenRestaurantAdmin(restaurant._id)
                              }
                            >
                              {t("franchiseOpenAdmin")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="franchise-portal__hint">
                {t("franchiseOpenHint")}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
